/**
 * The three Master Rave Plans (the user, 2026-10-13: "MyRave.io — Master Rave
 * Plans Modification Specification").
 *
 * A rave (one EDITION — each year is its own rave) holds exactly one plan, and
 * the plan says who decides what opening it costs:
 *
 *  - **FREE_RAVE** — free to open for every signed-in raver. No price, no
 *    paywall, no pricing screens for the producer.
 *  - **MYRAVE** — MyRave's own model: the one-rave price, the pack's credits,
 *    LITE / PLUS / MAX (`products.ts`). The producer sets nothing.
 *  - **INDEPENDENT_RAVE** — the producer sets the price, from a fixed ladder
 *    (`INDEPENDENT_PRICE_STEPS_CENTS`), or free; and only here may a producer
 *    make coupons. MyRave's own products (MAX, a tier's raves, pack credits)
 *    open it too (the user, 2026-10-13).
 *
 * The plans are FIXED here, in code: there is no table of plans, nothing to
 * create, rename or delete, and no screen that edits a definition. Only a
 * platform administrator ASSIGNS one to a rave (`PUT /admin/editions/:id/
 * master-plan`); the edition row (`Edition.masterPlan`) is the one place a
 * rave's plan is stored, and every screen and every access decision reads
 * it through the helpers below.
 */
import type { Product } from './products.js';

export const MASTER_RAVE_PLAN_KEYS = ['FREE_RAVE', 'MYRAVE', 'INDEPENDENT_RAVE'] as const;
export type MasterRavePlan = (typeof MASTER_RAVE_PLAN_KEYS)[number];

export interface MasterRavePlanDefinition {
  readonly key: MasterRavePlan;
  readonly name: string;
  /** May the rave's own admins set what opening it costs (and make coupons)? */
  readonly producerManagedPricing: boolean;
  readonly accessMode: 'FREE' | 'MYRAVE_BILLING' | 'PRODUCER_PRICING';
}

export const MASTER_RAVE_PLANS: Readonly<Record<MasterRavePlan, MasterRavePlanDefinition>> = Object.freeze({
  FREE_RAVE: Object.freeze({ key: 'FREE_RAVE', name: 'Free Rave', producerManagedPricing: false, accessMode: 'FREE' }),
  MYRAVE: Object.freeze({ key: 'MYRAVE', name: 'MyRave', producerManagedPricing: false, accessMode: 'MYRAVE_BILLING' }),
  INDEPENDENT_RAVE: Object.freeze({ key: 'INDEPENDENT_RAVE', name: 'Independent Rave', producerManagedPricing: true, accessMode: 'PRODUCER_PRICING' }),
} as const);

export const isMasterRavePlan = (v: unknown): v is MasterRavePlan =>
  typeof v === 'string' && (MASTER_RAVE_PLAN_KEYS as readonly string[]).includes(v);

/** The one rule every screen and route asks: may this rave's producer set its price? */
export const canManageIndependentPricing = (plan: MasterRavePlan): boolean =>
  MASTER_RAVE_PLANS[plan].producerManagedPricing;

/**
 * The prices an Independent Rave may ask, in US-dollar cents (the user,
 * 2026-10-13: "a price ladder"). Apple and Google sell only products made in
 * advance at fixed price points, so a typed price could not be sold inside the
 * phone app; each step is one product in both stores (`ravePriceProduct`) and
 * the same price on the web. 0 = free. Fixed in code, never a console setting:
 * a step must equal what the stores charge.
 */
export const INDEPENDENT_PRICE_STEPS_CENTS = [300, 500, 800, 1000, 1500, 2000, 3000] as const;

export const isIndependentPrice = (cents: unknown): cents is number =>
  cents === 0 || (INDEPENDENT_PRICE_STEPS_CENTS as readonly unknown[]).includes(cents);

/** Free to open? Derived from the plan (and an Independent rave's own price) — never stored twice. */
export const raveIsFree = (plan: MasterRavePlan, priceCents: number): boolean =>
  plan === 'FREE_RAVE' || (plan === 'INDEPENDENT_RAVE' && priceCents === 0);

/** A ladder step as a product id: `RAVE_PRICE_1500` for $15. */
export type RavePriceProductId = `RAVE_PRICE_${number}`;

export const ravePriceProductId = (cents: number): RavePriceProductId => `RAVE_PRICE_${cents}`;

/**
 * The product one ladder step is: one rave, for good, at that price — on the
 * web through Stripe, in the phone apps as `rave_price_<cents>` (a consumable,
 * like `rave_single`). Null for anything that is not a paid step.
 */
export function ravePriceProduct(cents: number): (Omit<Product, 'id'> & { id: RavePriceProductId }) | null {
  if (cents === 0 || !isIndependentPrice(cents)) return null;
  return { id: ravePriceProductId(cents), priceCents: cents, kind: 'one-time', gives: { rave: 'one' }, store: { productId: `rave_price_${cents}` } };
}

export const RAVE_PRICE_PRODUCTS = INDEPENDENT_PRICE_STEPS_CENTS.map((c) => ravePriceProduct(c)!);

export const isRavePriceProductId = (v: unknown): v is RavePriceProductId =>
  typeof v === 'string' && RAVE_PRICE_PRODUCTS.some((p) => p.id === v);
